// Repeat rules for scheduled tasks — browser twin of
// api/utils/tasks/recurrence.ts (CRA can't import from api/). Keep in step.
//
//   { type: 'days'|'weekdays'|'monthdays', every, weekdays: [], monthDays: [], until? }
//
// Weekdays are ISO: 1 = Monday ... 7 = Sunday. Dates are 'YYYY-MM-DD'.
import { RecurrenceRule } from '../types/tasks';
import { addDays, isoWeekday, parseDay } from './taskDates';

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const MAX_OCCURRENCES = 750;

export function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function recurrenceLabel(rule: RecurrenceRule | null | undefined): string | null {
  if (!rule) return null;
  if (rule.type === 'days') {
    const every = Number(rule.every) || 0;
    if (every < 1) return null;
    return every === 1 ? 'Daily' : every === 7 ? 'Weekly' : `Every ${every} days`;
  }
  if (rule.type === 'weekdays') {
    const days = rule.weekdays || [];
    if (!days.length) return null;
    if (days.length === 7) return 'Every day';
    if (days.length === 5 && !days.includes(6) && !days.includes(7)) return 'Weekdays';
    return 'Every ' + days.map((d) => WEEKDAYS[d - 1]).join(', ');
  }
  if (rule.type === 'monthdays') {
    const days = rule.monthDays || [];
    if (!days.length) return null;
    return days.map(ordinal).join(', ') + ' of each month';
  }
  return null;
}

export function isRuleComplete(rule: RecurrenceRule | null | undefined): boolean {
  return !!recurrenceLabel(rule);
}

/** Every date the rule produces in [rangeStart, rangeEnd]; nothing before `anchor`. */
export function expandOccurrences(
  rule: RecurrenceRule | null | undefined,
  rangeStart: string,
  rangeEnd: string,
  anchor: string | null = null
): string[] {
  if (!rule || !rule.type) return [];
  let start = rangeStart;
  if (anchor && anchor > start) start = anchor;
  let end = rangeEnd;
  if (rule.until && rule.until < end) end = rule.until;
  if (start > end) return [];

  const out: string[] = [];
  if (rule.type === 'days') {
    const every = Math.max(1, Math.floor(Number(rule.every) || 1));
    const base = anchor || start;
    const offset = Math.round((parseDay(start).getTime() - parseDay(base).getTime()) / 86400000);
    const stepsIn = offset <= 0 ? 0 : Math.ceil(offset / every);
    let cursor = addDays(base, stepsIn * every);
    while (cursor <= end && out.length < MAX_OCCURRENCES) {
      out.push(cursor);
      cursor = addDays(cursor, every);
    }
    return out;
  }

  let matches: ((day: string) => boolean) | null = null;
  if (rule.type === 'weekdays') {
    const days = (rule.weekdays || []).filter((d) => d >= 1 && d <= 7);
    if (days.length) matches = (day) => days.includes(isoWeekday(day));
  } else if (rule.type === 'monthdays') {
    const days = (rule.monthDays || []).filter((d) => d >= 1 && d <= 31);
    if (days.length) matches = (day) => days.includes(parseDay(day).getDate());
  }
  if (!matches) return [];

  let cursor = start;
  while (cursor <= end && out.length < MAX_OCCURRENCES) {
    if (matches(cursor)) out.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return out;
}
